import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextEditorStyle } from '../../../../../../../shared/components/text-editor/text-editor.component';

export interface InfoLayout3Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: TextEditorStyle;
}

/**
 * Preview component for the "Info Layout 3" template used in the website builder; renders image, title and body content with configurable styles and emits click events for CTAs.
 * @component
 * @selector app-info-layout-3-preview
 * @example
 *   <app-info-layout-3-preview [data]="sampleData" (buttonClick)="onButtonClick($event)"></app-info-layout-3-preview>
 * @input {InfoLayout3Data | null} data - Data model for the layout including image, title, body and CTA information. Optional.
 * @input {string | null} headingColor - CSS color for heading text; applied to title elements if provided.
 * @input {string | null} paragraphColor - CSS color for paragraph/body text; applied to body copy if provided.
 * @input {string | null} headingFont - Font family or class applied to heading text.
 * @input {string | null} bodyFont - Font family or class applied to body text.
 * @input {string | null} primaryColor - Primary accent color used for buttons or highlights.
 * @input {any} titleStyles - Additional inline styles or style object applied to the title element.
 * @output {{ link: string; event: Event }} buttonClick - Emitted when a call‑to‑action button is activated; provides the target link and the originating Event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): Validates a link and emits buttonClick with its payload when present.
 * - getImageSrc(): string: Resolves the best available image source from the data model (image.src or imageUrl).
 * - getImageAlt(): string: Returns an accessible alt text for the image, defaulting to "Car Wash" if none is provided.
 * - getImageObjectFit(): string: Maps the data's image scaletype to a CSS object-fit value (e.g., cover, contain, fill).
 *
 * Lifecycle hooks:
 * - This component does not implement Angular lifecycle hooks explicitly; it is a stateless preview component that reacts to input changes and emits events.
 */
@Component({
  selector: 'app-info-layout-3-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './info-layout-3.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./info-layout-3.component.scss'],
})
export class InfoLayout3PreviewComponent {
  @Input() data: InfoLayout3Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  /**
   * Resolve the appropriate CSS object-fit value from the component's image scaletype.
   * @example
   * etImageObjectFit()
   * 'cover'
   * @returns {string} Resolved CSS object-fit ('fill', 'contain', or 'cover').
   */
  getImageObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'cover';
    // Map API scaletype to CSS object-fit
    switch (scaletype) {
      case 'fill':
      case 'stretch':
        return 'fill';
      case 'fit':
        return 'contain';
      case 'cover':
        return 'cover';
      case 'contain':
        return 'contain';
      default:
        return 'cover';
    }
  }
}
